import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createDayJob } from "./createDayJob";
import AddressAutocomplete from "../Dashboard/AddressAutocomplete";
import { hasPremiumAccess } from "../../utils/access";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import {
  abandonAppFlow,
  completeAppFlow,
  startAppFlow,
} from "../../utils/appAnalytics";

  const DayJobForm = ({ userLocation, isLoaded, loadError, userProfile }) => {
  const navigate = useNavigate();
  const premiumAllowed = hasPremiumAccess(userProfile);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general_labor");
  const [payType, setPayType] = useState("flat");
  const [payAmount, setPayAmount] = useState("");
  const [duration, setDuration] = useState("");
  const [dateNeeded, setDateNeeded] = useState("");
  const [locationText, setLocationText] = useState("");
  const [selectedLatLng, setSelectedLatLng] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    startAppFlow("job_post_day", "job_post_started", {
      feature: "jobs",
      action: "post_day_job",
      abandonEventType: "job_post_abandoned",
    });

    return () => abandonAppFlow("job_post_day");
  }, []);

  const handlePlaceSelect = ({ lat, lng, formattedAddress }) => {
    setSelectedLatLng({ lat, lng });
    setLocationText(formattedAddress || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!requireVerifiedEmail(undefined, userProfile)) return;

    if (!premiumAllowed) {
  alert(
    "Premium is required to post day jobs. You can still browse and buy local products for free."
  );
  navigate("/subscribe");
  return;
}

    if (!title.trim() || !locationText.trim()) {
      alert("Please fill in the job title and location.");
      return;
    }

    if (
      payType !== "quote" &&
      payAmount !== "" &&
      Number.isNaN(Number(payAmount))
    ) {
      alert("Pay amount must be a number.");
      return;
    }

    const finalLat = selectedLatLng?.lat ?? userLocation?.lat ?? null;
    const finalLng = selectedLatLng?.lng ?? userLocation?.lng ?? null;

    if (finalLat == null || finalLng == null) {
      alert("Please choose a location from the address search or use your GPS location.");
      return;
    }

    try {
      setIsSubmitting(true);

      const dayJobRef = await createDayJob({
        title: title.trim(),
        description: description.trim(),
        category,
        payType,
        payAmount: payType === "quote" || payAmount === "" ? null : Number(payAmount),
        duration: duration.trim(),
        dateNeeded: dateNeeded || "",
        locationText: locationText.trim(),
        city: "",
        state: "",
        lat: finalLat,
        lng: finalLng,
      });

      completeAppFlow("job_post_day", "job_post_completed", {
        feature: "jobs",
        action: "post_day_job",
        relatedId: dayJobRef?.id || "",
      });

      alert("Day job posted!");

      setTitle("");
      setDescription("");
      setCategory("general_labor");
      setPayType("flat");
      setPayAmount("");
      setDuration("");
      setDateNeeded("");
      setLocationText("");
      setSelectedLatLng(null);
    } catch (error) {
      console.error("Error posting day job:", error);
      alert("Could not post day job. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="card" onSubmit={handleSubmit}>
      <h2>🛠 Post a Day Job</h2>


      <div className="context-tip">
        <strong>Tip:</strong> Be clear about the task, date, pay, safety needs,
        and who provides tools or transportation. Parents and hiring parties
        handle any minor work arrangements directly.
      </div>
      {!premiumAllowed && (
  <div className="info-bar" style={{ marginBottom: "12px" }}>
    <div className="info-panel-title">Premium required</div>
    <div className="info-panel-text">
      Browsing and buying local products is free. Posting day jobs requires
      Premium or a lifetime founder account.
    </div>

    <button
      type="button"
      className="btn-primary"
      onClick={() => navigate("/subscribe")}
      style={{ marginTop: "10px" }}
    >
      Upgrade to post jobs
    </button>
  </div>
)}
      
      <p style={{ marginTop: "-6px", opacity: 0.8 }}>
        
      </p>

      <input
        type="text"
        placeholder="Job Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <textarea
        placeholder="Describe the job..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={4}
      />

      <select value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="general_labor">General Labor</option>
        <option value="grass_cutting">Grass Cutting</option>
        <option value="bush hogging">Bush Hogging</option>
        <option value="fencing">Fencing</option>
        <option value="feeding">Feeding</option>
        <option value="repairs">Repairs</option>
        <option value="hauling">Hauling</option>
        <option value="animal_care">Animal Care</option>
        <option value="cleanup">Cleanup</option>
        <option value="equipment_help">Equipment Help</option>
        <option value="other">Other</option>
      </select>

      <select value={payType} onChange={(e) => setPayType(e.target.value)}>
        <option value="flat">Flat Pay</option>
        <option value="hourly">Hourly</option>
        <option value="quote">Open to Offers</option>
      </select>

      {payType !== "quote" && (
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder={payType === "hourly" ? "Hourly Pay ($)" : "Flat Pay ($)"}
          value={payAmount}
          onChange={(e) => setPayAmount(e.target.value)}
        />
      )}

      <input
        type="text"
        placeholder="Duration (example: 1 day, 4 hours)"
        value={duration}
        onChange={(e) => setDuration(e.target.value)}
      />
       <p>Start Date</p>
      <input
        type="date"
        value={dateNeeded}
        onChange={(e) => setDateNeeded(e.target.value)}
        />
        

      <div style={{ width: "100%" }}>
        <AddressAutocomplete
          isLoaded={isLoaded}
          loadError={loadError}
          onPlaceSelect={handlePlaceSelect}
        />
      </div>

      <input
        type="text"
        placeholder="Selected location"
        value={locationText}
        onChange={(e) => setLocationText(e.target.value)}
      />

      <button className="button" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Posting..." : "Post Job"}
      </button>
    </form>
  );
};

export default DayJobForm;
